import React, {useEffect, useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {OfficeBuildingIcon} from "@heroicons/react/outline";
import {formattedDateTime, secondsToHuman} from "services/date";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {Facility} from "types/Facility";
import {useNotifications} from "context/NotificationContext";
import {useNavigate, useSearchParams} from "react-router-dom";
import DeleteFacility from '../Delete';
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import FacilityContextMenu from "../../../components/menus/FacilityContextMenu";
import {getStoredItem, setStoredItem, STORAGE_KEYS} from "../../../services/storage";
import {isValidSiteId} from "../../../services/site";
import FloatingDropdown from "../../../components/FloatingDropdown";
import {Site} from "../../../types/Site";

// Types ----

type SiteItem = {
  label: string
  value: string | null
}

// Component ----

function FacilityList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    site: '',
  });

  const [deletingFacility, setDeletingFacility] = useState<Facility | null>(null);
  const [page, setPage] = useState<number>(1);

  const siteIdParam = searchParams.get('site') ?? '';

  const { data: facilities, isLoading: facilitiesLoading, isFetching: facilitiesFetching, refetch: refetchFacilities} = useQuery(
    ['facilities.all', {page, sites: siteIdParam}],
    () => ngage.facilities.all({page, sites: siteIdParam || undefined}),
    {keepPreviousData: true, enabled: auth.can('facilities:read')}
  )

  const {data: sites, isLoading: sitesLoading} = useQuery(['sites.all'], () => ngage.sites.all());

  // Check URL params or local storage for selected site id when sites are loaded
  useEffect(() => {
    if (!sites?.data) return;

    const storedSiteId = getStoredItem(STORAGE_KEYS.FACILITIES_SITE_ID);
    const defaultSiteId = storedSiteId ?? '';
    const siteId = isValidSiteId(sites.data, siteIdParam) ? siteIdParam : defaultSiteId;

    setSearchParams(currentParams => {
      currentParams.set('site', siteId);
      return currentParams;
    }, {replace: true});

  }, [siteIdParam, sites?.data, setSearchParams])

  // Create site items for dropdown
  function getSiteItems(sites: Site[] = []): SiteItem[] {
    const allSites: SiteItem = {label: `All ${termFor('sites')}`, value: null};
    const dynamicTypes = sites.map(site => ({label: site.name, value: site.id.toString()}));

    return [allSites, ...dynamicTypes];
  }

  // Get site items for dropdown
  const siteItems = sites?.data ? getSiteItems(sites.data) : [];

  // Set selected site
  function selectSite(siteId: number | null) {
    const siteIdString = siteId?.toString() ?? '';
    setSearchParams(currentParams => {
      currentParams.set('site', siteIdString);
      return currentParams;
    }, {replace: true});
    setStoredItem(STORAGE_KEYS.FACILITIES_SITE_ID, siteIdString);
  }

  // Prevent unauthorised viewing
  if (auth.cannot('facilities:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetch('facilities.all', {page}, ngage.facilities.all)

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('facilities:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/facilities/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {'App Content': '/', 'Site': '/', [termFor('Facilities')]: '/facilities'}

  return (
    <Page
      title={termFor('Facilities')}
      actions={actions}
      breadcrumbs={breadcrumbs}
      isLoading={facilitiesLoading || sitesLoading}
    >
      <div className="flex flex-col gap-5">
        <header className='flex justify-between gap-2 items-end'>
          {/* Refresh button */}
          <Button
            onClick={() => refetchFacilities()}
            showSpinner={facilitiesFetching}
            disabled={facilitiesFetching}
            size={Button.sizes.TINY}
            color={Button.colors.SECONDARY}
          >
            {facilitiesFetching ? 'Refreshing' : 'Refresh'}
          </Button>

          <div className='flex gap-2'>
            {/* Site filter */}
            <FloatingDropdown
              label={termFor(`Site`)}
              items={siteItems}
              selectedIndex={Math.max(siteItems.findIndex((item) => item.value === siteIdParam), 0)}
              onSelect={(item) => selectSite(item.value)}
            />
          </div>
        </header>

        <Table paginationData={facilities} setPage={setPage}>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Description</TableHeader>
              <TableHeader>{termFor('Zone')}</TableHeader>
              <TableHeader>{termFor('Site')}</TableHeader>
              <TableHeader>Avg. Dwell Duration</TableHeader>
              <TableHeader>Added</TableHeader>
              <TableHeader/>
            </TableRow>
          </TableHead>

          <TableBody isLoading={facilitiesLoading}>
            {facilities && facilities.data.length < 1 ?
              <TableRow index={0}>
                <TableData colSpan={7}>
                  <div className="py-4 flex flex-col justify-center items-center">
                    <OfficeBuildingIcon className="h-8 w-8 text-gray-400 mb-2"/>
                    <span className="text-sm font-medium text-gray-600">No {termFor('facilities')}</span>
                  </div>
                </TableData>
              </TableRow>
              : facilities?.data.map((facility, index) => (
                <TableRow key={facility.id} index={index} highlight>
                  <TableData>
                    <div className="flex">
                      <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                        <FacilityContextMenu
                          facility={facility}
                          onDeleteFacility={() => setDeletingFacility(facility)}
                          position="left"
                        />
                      </div>

                      <div className="flex group-hover:translate-x-1 transition-all">
                        <OfficeBuildingIcon className="h-5 w-5 mr-2 text-gray-500"/> {facility.name}
                      </div>
                    </div>
                  </TableData>
                  <TableData>{truncate(facility.description)}</TableData>
                  <TableData>{facility.zone.name}</TableData>
                  <TableData>{facility.zone.site.name}</TableData>
                  <TableData>{facility.average_dwell_duration ? secondsToHuman(facility.average_dwell_duration) : 'N/A'}</TableData>
                  <TableData>{formattedDateTime(facility.created_at)}</TableData>
                  <TableData alignment={TableData.alignments.RIGHT}>
                    <FacilityContextMenu
                      facility={facility}
                      onDeleteFacility={() => setDeletingFacility(facility)}
                    />
                  </TableData>

                  {/* Delete confirmation modal */}
                  <DeleteFacility
                    facility={deletingFacility}
                    show={facility === deletingFacility}
                    onCancel={() => setDeletingFacility(null)}
                    onDelete={facility => {
                      navigate('/facilities')
                      notify("success", `${termFor('Facility')} Deleted`, `${facility.name} has been permanently removed`)
                    }}
                    onDeleteFail={facility => {
                      setDeletingFacility(null)
                      notify("error", `${termFor('Facility')} Not Deleted`, `${facility.name} could not be removed`)
                    }}
                  />
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </Page>
  );
}

export default FacilityList;